import React, { useEffect, useState } from 'react';
import { SelectPicker, Table, DOMHelper, Stack, IconButton, Input, Whisper, Tooltip, Divider, SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { UserAchievement, AchievementPaginationFilterRequest, AchievementTypeList, UserAchievementPaginationFilterRequest } from '@/models/achievement.model';
import { autoCheckAchievements, getPagedUserAchievements } from '@/services/achievement.service';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@rsuite/icons/legacy/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { getCitiesLookup } from '@/services/city.service';
import { getCountriesLookup } from '@/services/country.service';
import ImageCell from '@/components/ImageCell';
import { MdRefresh } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import { jsDateToZone } from '@/utils/date-time';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;
interface UserAchievementListProps {
  userId: number;
}
const UserAchievementList : React.FC<UserAchievementListProps> = ({ userId }) => {
  const [filterReq, setFilterReq] = useState<UserAchievementPaginationFilterRequest>(new UserAchievementPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());  
  const [items, setItems] = useState<UserAchievement[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AchievementPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        userId
      };
      const result: PagedResponseModel<UserAchievement> = await getPagedUserAchievements(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching achievements:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };
  
  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };
  const handleAutoCheckAchievement = () => {
    autoCheckAchievements(userId).then(res => {
      if (res.success) {
        fetchData(filterReq);
        toastSuccessMessage('Achievements auto-checked successfully');
      } else {
        toastErrorMessage(`Error in auto-checking achievements: ${res.errorMessage}`);
      }
    }).catch(err => {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in auto-checking achievements: ${errorModel?.errorMessage}`);
    });
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />
          <SelectPicker
            label="Type"
            data={AchievementTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre =>({ ...pre, type: dataKey ?? undefined}))}
          />            
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
        <Stack>
          <IconButton icon={<Icon as={MdRefresh} />} onClick={handleAutoCheckAchievement} appearance="primary">Auto Achievements</IconButton>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Icon</HeaderCell>
          <ImageCell dataKey="profilePicture" />
        </Column>            
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Supporting City</HeaderCell>
          <Cell dataKey="supportingCity.name" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Supporting Country</HeaderCell>
          <Cell dataKey="supportingCountry.name" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Type</HeaderCell >
          <Cell dataKey='type'>
            {(rowData : UserAchievement) => AchievementTypeList.findLast(t => t.value == rowData.type)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={2} align="center" fullText sortable>
          <HeaderCell>Unique Number</HeaderCell>
          <Cell dataKey="uniqueNumber" />
        </Column>
        <Column flexGrow={2} align="center" fullText sortable>
          <HeaderCell>Achieved At</HeaderCell>
          <Cell dataKey='achievedAt'>
            {(rowData: UserAchievement) => jsDateToZone(rowData.achievedAt, undefined)}
          </Cell>
        </Column>
        <Column flexGrow={1} fixed="right" fullText>
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: UserAchievement) => 
              <Stack spacing={5}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit Achievement</Tooltip>}>
                  <IconButton onClick={() => nav('/achievements/' + rowData.id)} icon={<EditIcon />} circle size="sm" />
                </Whisper>
              </Stack>
            }
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default UserAchievementList;
